import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { useEffect } from "react";
import { createPortal } from "react-dom";
import {
  EMOTION_PRESETS,
  LIKERT_SCALE_PRESETS,
  LOW_RATING_SUGGESTION_SETS,
  RATING_SCALES,
  RATING_STYLES,
  TEXT_VARIANTS,
  buildLikertPoints,
  getLikertPreset,
  getLikertScalePoints,
  getLikertUniqueColumns,
  getLowRatingAllowOther,
  getLowRatingEnabled,
  getLowRatingPreset,
  getLowRatingThreshold,
  getMaxLength,
  getMinLength,
  getMultiChoiceDisplay,
  getSingleChoiceDisplay,
  getNumber,
  getPlaceholder,
  getRatingFollowUp,
  getRatingScale,
  getRatingStyle,
  getTextVariant
} from "./questionConfig";
function rawLowRatingOptions(cfg) {
  return Array.isArray(cfg?.low_rating_options) ? cfg.low_rating_options : [];
}
function newSuggestionKey() {
  return `opt_${Math.random().toString(36).slice(2, 8)}`;
}
function setKey(cfg, key, value) {
  const base = { ...cfg ?? {} };
  if (value === null || value === void 0 || value === "" || value === false) {
    delete base[key];
  } else {
    base[key] = value;
  }
  return Object.keys(base).length === 0 ? null : base;
}
function CustomizeQuestionModal({ q, onClose, onChangeConfig, onApplyEmotionPreset }) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);
  const cfg = q.config ?? null;
  return createPortal(
    /* @__PURE__ */ jsx(
      "div",
      {
        className: "modal-backdrop modal-backdrop--customize",
        role: "dialog",
        "aria-modal": "true",
        "aria-labelledby": "customize-modal-title",
        onMouseDown: (e) => {
          if (e.target === e.currentTarget) onClose();
        },
        children: /* @__PURE__ */ jsxs("div", { className: `modal-card card modal-card--customize${q.type === "rating" && getLowRatingEnabled(cfg) ? " modal-card--customize-wide" : ""}`, onMouseDown: (e) => e.stopPropagation(), children: [
          /* @__PURE__ */ jsxs("div", { className: "customize-modal-head", children: [
            /* @__PURE__ */ jsx("h2", { id: "customize-modal-title", children: "Customize question" }),
            q.label && /* @__PURE__ */ jsx("p", { className: "customize-modal-question", title: q.label, children: q.label }),
            /* @__PURE__ */ jsxs("p", { className: "muted small", children: [
              "Tailor how respondents see this question. These settings apply to ",
              /* @__PURE__ */ jsx("strong", { children: "this question only" }),
              "."
            ] })
          ] }),
          /* @__PURE__ */ jsxs("div", { className: "customize-modal-body", children: [
            q.type === "text" && /* @__PURE__ */ jsxs(Fragment, { children: [
              /* @__PURE__ */ jsx(Field, { label: "Input type", children: /* @__PURE__ */ jsx(
                "select",
                {
                  value: getTextVariant(cfg),
                  onChange: (e) => onChangeConfig(setKey(cfg, "variant", e.target.value === "text" ? null : e.target.value)),
                  children: TEXT_VARIANTS.map((v) => /* @__PURE__ */ jsx("option", { value: v.value, children: v.label }, v.value))
                }
              ) }),
              /* @__PURE__ */ jsx(Field, { label: "Placeholder text", children: /* @__PURE__ */ jsx(
                "input",
                {
                  type: "text",
                  value: getPlaceholder(cfg) ?? "",
                  onChange: (e) => onChangeConfig(setKey(cfg, "placeholder", e.target.value.slice(0, 200))),
                  placeholder: "e.g. you@example.com"
                }
              ) }),
              /* @__PURE__ */ jsxs(Row, { children: [
                /* @__PURE__ */ jsx(Field, { label: "Min length", children: /* @__PURE__ */ jsx(
                  "input",
                  {
                    type: "number",
                    min: 0,
                    value: getMinLength(cfg) ?? "",
                    onChange: (e) => onChangeConfig(setKey(cfg, "min_length", e.target.value ? Number(e.target.value) : null))
                  }
                ) }),
                /* @__PURE__ */ jsx(Field, { label: "Max length", children: /* @__PURE__ */ jsx(
                  "input",
                  {
                    type: "number",
                    min: 1,
                    value: getMaxLength(cfg) ?? "",
                    onChange: (e) => onChangeConfig(setKey(cfg, "max_length", e.target.value ? Number(e.target.value) : null))
                  }
                ) })
              ] }),
              getTextVariant(cfg) === "number" && /* @__PURE__ */ jsxs(Row, { children: [
                /* @__PURE__ */ jsx(Field, { label: "Min value", children: /* @__PURE__ */ jsx(
                  "input",
                  {
                    type: "number",
                    value: getNumber(cfg, "min") ?? "",
                    onChange: (e) => onChangeConfig(setKey(cfg, "min", e.target.value === "" ? null : Number(e.target.value)))
                  }
                ) }),
                /* @__PURE__ */ jsx(Field, { label: "Max value", children: /* @__PURE__ */ jsx(
                  "input",
                  {
                    type: "number",
                    value: getNumber(cfg, "max") ?? "",
                    onChange: (e) => onChangeConfig(setKey(cfg, "max", e.target.value === "" ? null : Number(e.target.value)))
                  }
                ) }),
                /* @__PURE__ */ jsx(Field, { label: "Step", children: /* @__PURE__ */ jsx(
                  "input",
                  {
                    type: "number",
                    step: "any",
                    value: getNumber(cfg, "step") ?? "",
                    onChange: (e) => onChangeConfig(setKey(cfg, "step", e.target.value === "" ? null : Number(e.target.value)))
                  }
                ) })
              ] })
            ] }),
            q.type === "rating" && /* @__PURE__ */ jsxs("div", { className: `customize-rating-layout${getLowRatingEnabled(cfg) ? " is-split" : ""}`, children: [
              /* @__PURE__ */ jsxs("div", { className: "customize-rating-main", children: [
              /* @__PURE__ */ jsx(Field, { label: "Scale", children: /* @__PURE__ */ jsx(
                "select",
                {
                  value: getRatingScale(cfg),
                  onChange: (e) => {
                    const next = Number(e.target.value);
                    onChangeConfig(setKey(cfg, "scale", next === 5 ? null : next));
                  },
                  children: RATING_SCALES.map((n) => /* @__PURE__ */ jsxs("option", { value: n, children: [
                    "1\u2013",
                    n
                  ] }, n))
                }
              ) }),
              /* @__PURE__ */ jsx(Field, { label: "Display style", children: /* @__PURE__ */ jsx("div", { className: "customize-style-grid", children: RATING_STYLES.map((s) => /* @__PURE__ */ jsxs(
                "label",
                {
                  className: `customize-style-tile${getRatingStyle(cfg) === s.value ? " is-active" : ""}`,
                  children: [
                    /* @__PURE__ */ jsx(
                      "input",
                      {
                        type: "radio",
                        name: "rating-style",
                        value: s.value,
                        checked: getRatingStyle(cfg) === s.value,
                        onChange: () => onChangeConfig(setKey(cfg, "style", s.value === "numbers" ? null : s.value))
                      }
                    ),
                    /* @__PURE__ */ jsx("span", { className: "customize-style-preview", children: s.preview }),
                    /* @__PURE__ */ jsx("span", { children: s.label })
                  ]
                },
                s.value
              )) }) }),
              /* @__PURE__ */ jsxs("label", { className: "customize-checkbox customize-checkbox--solo", children: [
                /* @__PURE__ */ jsx(
                  "input",
                  {
                    type: "checkbox",
                    checked: getRatingFollowUp(cfg),
                    onChange: (e) => onChangeConfig(setKey(cfg, "rating_follow_up", e.target.checked ? true : null))
                  }
                ),
                "Ask for an optional brief reason after the respondent selects a rating"
              ] }),
              /* @__PURE__ */ jsxs("label", { className: "customize-checkbox customize-checkbox--solo", children: [
                /* @__PURE__ */ jsx(
                  "input",
                  {
                    type: "checkbox",
                    checked: getLowRatingEnabled(cfg),
                    onChange: (e) => {
                      if (e.target.checked) {
                        const base = { ...cfg ?? {}, low_rating: true };
                        if (rawLowRatingOptions(base).length === 0) {
                          base.low_rating_preset = "general";
                          base.low_rating_options = LOW_RATING_SUGGESTION_SETS.general.options.map((o) => ({ ...o }));
                        }
                        onChangeConfig(base);
                      } else {
                        const next = { ...cfg ?? {} };
                        delete next.low_rating;
                        delete next.low_rating_preset;
                        delete next.low_rating_threshold;
                        delete next.low_rating_options;
                        delete next.low_rating_hide_other;
                        onChangeConfig(Object.keys(next).length ? next : null);
                      }
                    }
                  }
                ),
                "Ask what went wrong when the respondent gives a low rating"
              ] })
              ] }),
              getLowRatingEnabled(cfg) && /* @__PURE__ */ jsxs("div", { className: "customize-lowrating", children: [
                /* @__PURE__ */ jsx(Hint, { children: "Respondents who give a low score will be asked to pick what went wrong. Set when it triggers, load a starter set for your area, then edit the list." }),
                /* @__PURE__ */ jsxs(Row, { children: [
                  /* @__PURE__ */ jsx(Field, { label: "Trigger when rating is", children: /* @__PURE__ */ jsx(
                    "select",
                    {
                      value: getLowRatingThreshold(cfg),
                      onChange: (e) => onChangeConfig(setKey({ ...cfg ?? {}, low_rating: true }, "low_rating_threshold", Number(e.target.value) === 3 ? null : Number(e.target.value))),
                      children: Array.from({ length: Math.max(1, getRatingScale(cfg) - 1) }, (_, i) => i + 1).map((n) => /* @__PURE__ */ jsxs("option", { value: n, children: [
                        n,
                        " or lower"
                      ] }, n))
                    }
                  ) }),
                  /* @__PURE__ */ jsx(Field, { label: "Load a starter set", children: /* @__PURE__ */ jsxs(
                    "select",
                    {
                      value: getLowRatingPreset(cfg) ?? "",
                      onChange: (e) => {
                        const key = e.target.value;
                        if (!key || !LOW_RATING_SUGGESTION_SETS[key]) return;
                        onChangeConfig({ ...cfg ?? {}, low_rating: true, low_rating_preset: key, low_rating_options: LOW_RATING_SUGGESTION_SETS[key].options.map((o) => ({ ...o })) });
                      },
                      children: [
                        /* @__PURE__ */ jsx("option", { value: "", children: "Choose an area…" }, "_none"),
                        ...Object.keys(LOW_RATING_SUGGESTION_SETS).map((key) => /* @__PURE__ */ jsx("option", { value: key, children: LOW_RATING_SUGGESTION_SETS[key].label }, key))
                      ]
                    }
                  ) })
                ] }),
                /* @__PURE__ */ jsx(Hint, { children: "Loading a starter set replaces the suggestions below." }),
                /* @__PURE__ */ jsx(Field, { label: "Suggestions shown to respondents", children: /* @__PURE__ */ jsxs("div", { className: "customize-lowrating-options", children: [
                  rawLowRatingOptions(cfg).length === 0 && /* @__PURE__ */ jsx("p", { className: "muted small customize-lowrating-empty", children: "No suggestions yet — load a starter set above or add your own." }),
                  rawLowRatingOptions(cfg).map((o, i) => /* @__PURE__ */ jsxs("div", { className: "customize-lowrating-option", children: [
                    /* @__PURE__ */ jsx("span", { className: "customize-lowrating-bullet", "aria-hidden": true, children: "•" }),
                    /* @__PURE__ */ jsx(
                      "input",
                      {
                        type: "text",
                        value: o?.label ?? "",
                        placeholder: "Suggestion label",
                        onChange: (e) => {
                          const opts = rawLowRatingOptions(cfg).map((it, idx) => idx === i ? { key: it?.key || newSuggestionKey(), label: e.target.value.slice(0, 120) } : it);
                          onChangeConfig({ ...cfg ?? {}, low_rating: true, low_rating_options: opts });
                        }
                      }
                    ),
                    /* @__PURE__ */ jsx(
                      "button",
                      {
                        type: "button",
                        className: "customize-lowrating-remove",
                        "aria-label": "Remove suggestion",
                        title: "Remove",
                        onClick: () => {
                          const opts = rawLowRatingOptions(cfg).filter((_, idx) => idx !== i);
                          onChangeConfig({ ...cfg ?? {}, low_rating: true, low_rating_options: opts });
                        },
                        children: "✕"
                      }
                    )
                  ] }, o?.key ?? i)),
                  /* @__PURE__ */ jsx(
                    "button",
                    {
                      type: "button",
                      className: "customize-lowrating-add",
                      onClick: () => {
                        const opts = [...rawLowRatingOptions(cfg), { key: newSuggestionKey(), label: "" }];
                        onChangeConfig({ ...cfg ?? {}, low_rating: true, low_rating_options: opts });
                      },
                      children: "+ Add suggestion"
                    }
                  )
                ] }) }),
                /* @__PURE__ */ jsxs("label", { className: "customize-checkbox customize-checkbox--solo", children: [
                  /* @__PURE__ */ jsx(
                    "input",
                    {
                      type: "checkbox",
                      checked: getLowRatingAllowOther(cfg),
                      onChange: (e) => onChangeConfig(setKey({ ...cfg ?? {}, low_rating: true }, "low_rating_hide_other", e.target.checked ? null : true))
                    }
                  ),
                  "Include an “Other (please specify)” box"
                ] })
              ] })
            ] }),
            q.type === "multi" && /* @__PURE__ */ jsxs(Fragment, { children: [
              /* @__PURE__ */ jsxs("label", { className: "customize-checkbox customize-checkbox--solo", children: [
                /* @__PURE__ */ jsx(
                  "input",
                  {
                    type: "checkbox",
                    checked: getMultiChoiceDisplay(cfg) === "dropdown",
                    onChange: (e) => {
                      if (e.target.checked) {
                        onChangeConfig(setKey(cfg, "choice_display", "dropdown"));
                      } else {
                        const next = { ...cfg ?? {} };
                        delete next.choice_display;
                        delete next.placeholder;
                        onChangeConfig(Object.keys(next).length ? next : null);
                      }
                    }
                  }
                ),
                "Show answers as a dropdown"
              ] }),
              getMultiChoiceDisplay(cfg) === "dropdown" && /* @__PURE__ */ jsxs(Fragment, { children: [
                /* @__PURE__ */ jsx(Hint, { children: "Respondents open the list and tick one or more options\u2014no keyboard shortcut needed." }),
                /* @__PURE__ */ jsx(Field, { label: "Optional prompt above list", children: /* @__PURE__ */ jsx(
                  "input",
                  {
                    type: "text",
                    value: getPlaceholder(cfg) ?? "",
                    onChange: (e) => onChangeConfig(setKey(cfg, "placeholder", e.target.value.slice(0, 200))),
                    placeholder: "e.g. Select all that apply"
                  }
                ) })
              ] })
            ] }),
            q.type === "single" && /* @__PURE__ */ jsxs(Fragment, { children: [
              /* @__PURE__ */ jsxs("label", { className: "customize-checkbox customize-checkbox--solo", children: [
                /* @__PURE__ */ jsx(
                  "input",
                  {
                    type: "checkbox",
                    checked: getSingleChoiceDisplay(cfg) === "dropdown",
                    onChange: (e) => {
                      if (e.target.checked) {
                        onChangeConfig(setKey(cfg, "choice_display", "dropdown"));
                      } else {
                        const next = { ...cfg ?? {} };
                        delete next.choice_display;
                        delete next.placeholder;
                        onChangeConfig(Object.keys(next).length ? next : null);
                      }
                    }
                  }
                ),
                "Show answers as a dropdown"
              ] }),
              getSingleChoiceDisplay(cfg) === "dropdown" && /* @__PURE__ */ jsxs(Fragment, { children: [
                /* @__PURE__ */ jsx(Hint, { children: "Respondents open the list and select one option from the dropdown." }),
                /* @__PURE__ */ jsx(Field, { label: "Optional prompt above list", children: /* @__PURE__ */ jsx(
                  "input",
                  {
                    type: "text",
                    value: getPlaceholder(cfg) ?? "",
                    onChange: (e) => onChangeConfig(setKey(cfg, "placeholder", e.target.value.slice(0, 200))),
                    placeholder: "e.g. Please select an option"
                  }
                ) })
              ] })
            ] }),

            q.type === "emotion" && /* @__PURE__ */ jsx(Fragment, { children: /* @__PURE__ */ jsxs(Field, { label: "Pick a preset (replaces current options)", children: [
              /* @__PURE__ */ jsx("div", { className: "customize-preset-list", children: Object.keys(EMOTION_PRESETS).map((key) => /* @__PURE__ */ jsx(
                "button",
                {
                  type: "button",
                  className: `btn small-btn${cfg?.preset === key ? " primary" : ""}`,
                  onClick: () => onApplyEmotionPreset(key),
                  children: EMOTION_PRESETS[key].label
                },
                key
              )) }),
              /* @__PURE__ */ jsx(Hint, { children: "You can still hand-edit individual option labels in the question card after applying a preset." })
            ] }) }),

            q.type === "likert" && (() => {
              const preset = getLikertPreset(cfg);
              const points = getLikertScalePoints(cfg);
              const activeCount = LIKERT_SCALE_PRESETS[preset].counts.includes(points.length) ? points.length : LIKERT_SCALE_PRESETS[preset].counts[0];
              // Preset/size changes rebuild the scale; the default
              // (agreement, 5 points) is stored as no config at all,
              // mirroring the server's normalize_config.
              const applyScale = (nextPreset, count) => {
                const built = buildLikertPoints(nextPreset, count);
                const next = { ...cfg ?? {} };
                if (nextPreset === "agreement") delete next.scale_preset;
                else next.scale_preset = nextPreset;
                if (nextPreset === "agreement" && built.length === 5) delete next.scale_points;
                else next.scale_points = built;
                onChangeConfig(Object.keys(next).length ? next : null);
              };
              const patchPointLabel = (i, label) => {
                const next = { ...cfg ?? {} };
                next.scale_points = points.map((p, idx) => idx === i ? { ...p, label: label.slice(0, 120) } : { ...p });
                onChangeConfig(next);
              };
              const presetPreview = (key) => {
                const pts = buildLikertPoints(key, 5);
                if (key === "numeric") return pts.map((p) => p.label).join(" ");
                return `${pts[0].label} → ${pts[pts.length - 1].label}`;
              };
              const presetName = (key) => LIKERT_SCALE_PRESETS[key].label.split(" (")[0];
              const tooManyStatements = q.options.length > points.length;
              return /* @__PURE__ */ jsxs(Fragment, { children: [
                /* @__PURE__ */ jsxs("div", { className: "customize-section", children: [
                  /* @__PURE__ */ jsx("div", { className: "customize-section-title", children: "Scale type" }),
                  /* @__PURE__ */ jsx("div", { className: "customize-style-grid", children: Object.keys(LIKERT_SCALE_PRESETS).map((key) => /* @__PURE__ */ jsxs(
                    "label",
                    {
                      className: `customize-style-tile${preset === key ? " is-active" : ""}`,
                      children: [
                        /* @__PURE__ */ jsx(
                          "input",
                          {
                            type: "radio",
                            name: "likert-preset",
                            value: key,
                            checked: preset === key,
                            onChange: () => applyScale(key, activeCount)
                          }
                        ),
                        /* @__PURE__ */ jsx("span", { children: presetName(key) }),
                        /* @__PURE__ */ jsx("span", { className: "muted small customize-likert-tile-preview", children: presetPreview(key) })
                      ]
                    },
                    key
                  )) })
                ] }),
                /* @__PURE__ */ jsxs("div", { className: "customize-section", children: [
                  /* @__PURE__ */ jsx("div", { className: "customize-section-title", children: "Scale size" }),
                  /* @__PURE__ */ jsx("div", { className: "customize-segmented", role: "group", "aria-label": "Scale size", children: LIKERT_SCALE_PRESETS[preset].counts.map((n) => /* @__PURE__ */ jsx(
                    "button",
                    {
                      type: "button",
                      className: `customize-segment${activeCount === n ? " is-active" : ""}`,
                      "aria-pressed": activeCount === n,
                      onClick: () => applyScale(preset, n),
                      children: preset === "numeric" ? `1–${n}` : `${n} points`
                    },
                    n
                  )) })
                ] }),
                /* @__PURE__ */ jsxs("div", { className: "customize-section", children: [
                  /* @__PURE__ */ jsx("div", { className: "customize-section-title", children: "Respondents will see" }),
                  /* @__PURE__ */ jsx("div", { className: "customize-likert-preview", children: points.map((p) => /* @__PURE__ */ jsx("span", { className: "customize-likert-chip", children: p.label }, p.key)) }),
                  preset !== "numeric" && /* @__PURE__ */ jsx("div", { className: "customize-likert-points", children: points.map((p, i) => /* @__PURE__ */ jsxs("div", { className: "customize-likert-point-row", children: [
                    /* @__PURE__ */ jsx("span", { className: "customize-likert-point-num", "aria-hidden": true, children: i + 1 }),
                    /* @__PURE__ */ jsx(
                      "input",
                      {
                        type: "text",
                        value: p.label,
                        "aria-label": `Label for scale point ${i + 1}`,
                        onChange: (e) => patchPointLabel(i, e.target.value)
                      }
                    )
                  ] }, p.key)) }),
                  preset !== "numeric" && /* @__PURE__ */ jsx(Hint, { children: "Edit any label — the preview above updates as you type." })
                ] }),
                /* @__PURE__ */ jsxs("div", { className: "customize-section", children: [
                  /* @__PURE__ */ jsx("div", { className: "customize-section-title", children: "Answer rules" }),
                  /* @__PURE__ */ jsxs("label", { className: "customize-checkbox customize-checkbox--solo", children: [
                    /* @__PURE__ */ jsx(
                      "input",
                      {
                        type: "checkbox",
                        checked: getLikertUniqueColumns(cfg),
                        onChange: (e) => onChangeConfig(setKey(cfg, "unique_columns", e.target.checked ? true : null))
                      }
                    ),
                    "Each scale value can be picked only once across all statements (forced ranking)"
                  ] }),
                  getLikertUniqueColumns(cfg) && (tooManyStatements ? /* @__PURE__ */ jsx("p", { className: "customize-likert-warning", role: "alert", children: `${q.options.length} statements but only ${points.length} scale values — respondents won't be able to answer every row. Add scale points or remove statements.` }) : /* @__PURE__ */ jsx(Hint, { children: "Each column works like a single select — picking a value that's already used on another statement moves it to the one you clicked. Ideal for ranking a list of items." }))
                ] })
              ] });
            })()
          ] }),
          /* @__PURE__ */ jsxs("div", { className: "builder-modal-actions customize-modal-actions", children: [
            /* @__PURE__ */ jsx("button", { type: "button", className: "btn", onClick: () => onChangeConfig(null), children: "Reset to defaults" }),
            /* @__PURE__ */ jsx("button", { type: "button", className: "btn primary", onClick: onClose, children: "Done" })
          ] })
        ] })
      }
    ),
    document.body
  );
}
function Field({ label, children }) {
  return /* @__PURE__ */ jsxs("label", { className: "field customize-field", children: [
    /* @__PURE__ */ jsx("span", { children: label }),
    children
  ] });
}
function Row({ children }) {
  return /* @__PURE__ */ jsx("div", { className: "customize-field-row", children });
}
function Hint({ children }) {
  return /* @__PURE__ */ jsx("span", { className: "muted small customize-hint", children });
}
export {
  CustomizeQuestionModal
};
